/**
 * Detección "¿estamos dentro del iframe de Whatsfy o en standalone?".
 *
 * Implicaciones de UX cuando isEmbedded() === true:
 *   - No mostrar logout: la sesión se autocrea desde el handshake del embed
 *     (account_id + admin_email). Cerrar sesión solo rompe el flujo —
 *     apenas el user vuelve a abrir el módulo desde Whatsfy, se relogea.
 *   - El módulo está embebido en una página que NO controlamos; cualquier
 *     "navegar fuera" (ej. <Link to="/login">) lleva al user a una pantalla
 *     que solo existe para el modo standalone.
 *
 * Por qué el try/catch:
 *   En navegadores que aplican same-origin estricto, leer window.top desde
 *   un iframe cross-origin puede lanzar SecurityError. Eso por sí solo es
 *   evidencia suficiente de que estamos embebidos en otro origen.
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Cache del embed handshake (account_id + user_email) en sessionStorage.
 * Cuando el iframe se carga por primera vez con `?account_id=...&user_email=...`,
 * guardamos esos values. Si después la cookie del JWT expira y el refresh
 * también falla, podemos re-aprovisionar la sesión llamando a /embed con los
 * params cacheados — sin caer al /login standalone (que dentro del iframe es
 * un dead-end: el user ve un form de magic link sin contexto del tenant).
 *
 * El cache vive 1 hora — si pasó más, asumimos que el contexto del iframe
 * cambió (otro tenant, otro admin) y forzamos el flow normal.
 */

const STORAGE_KEY = 'inventario_embed_handshake';
const MAX_AGE_MS = 60 * 60 * 1000;

export interface EmbedHandshake {
  accountId: string;
  userEmail: string;
  accountName?: string;
  locale?: string;
  tz?: string;
  capturedAt: number;
}

export function captureEmbedHandshake(input: {
  accountId: string;
  userEmail: string;
  accountName?: string | null;
  locale?: string | null;
  tz?: string | null;
}): void {
  if (!input.accountId || !input.userEmail) return;
  const handshake: EmbedHandshake = {
    accountId: input.accountId,
    userEmail: input.userEmail,
    accountName: input.accountName ?? undefined,
    locale: input.locale ?? undefined,
    tz: input.tz ?? undefined,
    capturedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handshake));
  } catch {
    // sessionStorage puede fallar en modo privado o Safari ITP — silent.
  }
}

export function getEmbedHandshake(): EmbedHandshake | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const h = JSON.parse(raw) as EmbedHandshake;
    if (Date.now() - h.capturedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return h;
  } catch {
    return null;
  }
}

/**
 * Lee el `account_id` del Whatsfy panel directamente desde `document.referrer`.
 *
 * Cross-origin restrictions del navegador bloquean leer `parent.location.href`,
 * pero el referrer del iframe SÍ está disponible y contiene la URL del panel
 * host (ej. `https://app.whatsfy.co/en/l?acc=1608714`). Lo usamos como capa 2
 * de defensa para detectar mismatch cuando el user cambia de cuenta en el
 * panel pero la cookie httpOnly del módulo apunta al tenant viejo.
 *
 * Devuelve null si:
 *   - No hay referrer (acceso directo standalone, no iframe).
 *   - El referrer no es una URL parseable.
 *   - El referrer no trae el query `acc` ni `account_id`.
 */
export function getReferrerAccountId(): string | null {
  try {
    if (typeof document === 'undefined' || !document.referrer) return null;
    const url = new URL(document.referrer);
    // Whatsfy panel usa `acc=`; otros distribuidores podrían usar
    // `account_id=` — soportamos ambos sin acoplar a un host concreto.
    const acc = url.searchParams.get('acc') ?? url.searchParams.get('account_id');
    return acc && acc.length > 0 ? acc : null;
  } catch {
    return null;
  }
}

/**
 * Construye la URL de re-embed para re-aprovisionar la sesión cuando el
 * JWT expiró dentro del iframe. Devuelve null si no tenemos params en cache.
 */
export function buildReembedUrl(apiBase: string): string | null {
  const h = getEmbedHandshake();
  if (!h) return null;
  const params = new URLSearchParams({
    account_id: h.accountId,
    user_email: h.userEmail,
  });
  if (h.accountName) params.set('account_name', h.accountName);
  if (h.locale) params.set('locale', h.locale);
  if (h.tz) params.set('tz', h.tz);
  return `${apiBase}/embed?${params.toString()}`;
}

/**
 * IFRAME-FIX-V2: postMessage opt-in del parent Whatsfy hacia el iframe del
 * módulo cuando el comerciante cambia de cuenta en el panel host. Sin esto,
 * el iframe queda mostrando data del tenant viejo (cross-origin bloquea
 * detección unilateral desde el iframe). Capa 1 del fix; complementada por
 * el badge "Cuenta: {tenantName}" en el header (capa 2 — siempre visible
 * aunque el snippet del parent no esté inyectado).
 *
 * El parent debe pegar el snippet documentado en
 * `docs/whatsfy-injection-snippets.md` para activar la capa 1.
 */
const TRUSTED_ORIGINS: RegExp[] = [
  /^https:\/\/(.+\.)?whatsfy\.co$/,
  /^https:\/\/(.+\.)?chatrace\.com$/,
];

/**
 * Valida que el origin del MessageEvent pertenece a un host conocido del
 * panel Whatsfy / ChatRace. Cross-origin postMessage NO valida origin por
 * sí solo — el listener DEBE filtrar manualmente o cualquier iframe
 * adversario puede inyectar `whatsfy:account-change` y forzar redirect.
 *
 * Solo permitimos `https://` — `http://app.whatsfy.co` no entra (el panel
 * real siempre es TLS).
 */
export function isTrustedOrigin(origin: string): boolean {
  return TRUSTED_ORIGINS.some((re) => re.test(origin));
}

export interface AccountChangeMessage {
  type: 'whatsfy:account-change';
  accountId: string | null;
}

/**
 * Suscribe un listener al `message` del window que filtra por origin
 * confiable + shape exacto del payload. Devuelve función cleanup para usar
 * desde `useEffect`. El callback recibe el accountId nuevo (string) o null
 * cuando el parent no pudo extraerlo del query string del panel.
 */
export function listenAccountChange(
  onChange: (accountId: string | null) => void,
): () => void {
  const handler = (event: MessageEvent) => {
    if (!isTrustedOrigin(event.origin)) return;
    const data = event.data;
    if (typeof data !== 'object' || data === null) return;
    const msg = data as Partial<AccountChangeMessage>;
    if (msg.type !== 'whatsfy:account-change') return;
    const acc = typeof msg.accountId === 'string' ? msg.accountId : null;
    onChange(acc);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
